import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-3 — useFinanceContas.
//
// Agregador de dados pra Contas tab. Faz 4 fetches em paralelo:
//   /api/accounts
//   /api/accounts/total-balance
//   /api/accounts/credit-usage
//   /api/accounts/spending-by-card?reference_month=YYYY-MM
//
// Detecção Open Finance: qualquer account com pluggy_item_id !== null
// liga modo OF ON. Default OFF (todas accounts manuais).
//
// CRUD: createAccount, updateAccount, deleteAccount usam authFetch +
// refetch da lista. Optimistic é responsabilidade do caller (modal).
//
// Fatura estimada/prediction NÃO é fetchada aqui — lazy nos cartões.

const BANK_TYPES = new Set(["checking", "savings", "cash", "other"]);

async function _safeJson(url) {
  try {
    const r = await authFetch(url);
    if (!r.ok) return { error: `HTTP ${r.status}`, data: null };
    return { error: null, data: await r.json() };
  } catch (e) {
    return { error: String(e.message || e), data: null };
  }
}

function _currentMonthIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function useFinanceContas({ referenceMonth } = {}) {
  const [accounts, setAccounts] = useState([]);
  const [totalBalance, setTotalBalance] = useState(null);
  const [creditUsage, setCreditUsage] = useState(null);
  const [spendingByCard, setSpendingByCard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refMonth = referenceMonth || _currentMonthIso();

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const [a, tb, cu, sbc] = await Promise.all([
      _safeJson(`${API_URL}/api/accounts`),
      _safeJson(`${API_URL}/api/accounts/total-balance`),
      _safeJson(`${API_URL}/api/accounts/credit-usage`),
      _safeJson(`${API_URL}/api/accounts/spending-by-card?reference_month=${refMonth}`),
    ]);
    if (a.data) setAccounts(Array.isArray(a.data?.accounts) ? a.data.accounts : []);
    if (tb.data) setTotalBalance(tb.data);
    if (cu.data) setCreditUsage(cu.data);
    if (sbc.data) setSpendingByCard(sbc.data);

    const errs = [a.error, tb.error, cu.error, sbc.error].filter(Boolean);
    if (errs.length) setError(errs[0]);
    setIsLoading(false);
  }, [refMonth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const creditCards = useMemo(
    () => accounts.filter(a => a.type === "credit_card"),
    [accounts],
  );
  const bankAccounts = useMemo(
    () => accounts.filter(a => BANK_TYPES.has(a.type)),
    [accounts],
  );

  // OF ON: qualquer account com pluggy_item_id setado.
  const isOpenFinance = useMemo(
    () => accounts.some(a => Boolean(a.pluggy_item_id)),
    [accounts],
  );

  const distinctInstitutions = useMemo(() => {
    const s = new Set();
    accounts.forEach(a => { if (a.institution) s.add(a.institution); });
    return s.size;
  }, [accounts]);

  // CRUD
  const createAccount = useCallback(async (payload) => {
    const r = await authFetch(`${API_URL}/api/accounts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    await fetchAll();
    return fresh;
  }, [fetchAll]);

  const updateAccount = useCallback(async (id, patch) => {
    const r = await authFetch(`${API_URL}/api/accounts/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    // Patch otimista local pra resposta imediata, depois refetch agregados
    setAccounts(prev => prev.map(a => (a.id === id ? { ...a, ...fresh } : a)));
    fetchAll();
    return fresh;
  }, [fetchAll]);

  const deleteAccount = useCallback(async (id) => {
    const r = await authFetch(`${API_URL}/api/accounts/${id}`, { method: "DELETE" });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    await fetchAll();
  }, [fetchAll]);

  return {
    accounts,
    creditCards,
    bankAccounts,
    totalBalance,
    creditUsage,
    spendingByCard,
    isOpenFinance,
    distinctInstitutions,
    isLoading,
    error,
    refetch: fetchAll,
    createAccount,
    updateAccount,
    deleteAccount,
  };
}
